import React, { useEffect, useId, useMemo } from 'react';
import ReactModal from 'react-modal';

import { Props as FlexProps } from '@rover/kibble/core/Flex/Flex';
import NoScrollController from '@rover/kibble/official-patterns/Modal/utils/NoScrollController';
import type { ThemeHeadingSizes } from '@rover/kibble/styles';

import { ButtonVariants } from '../../core/Button';
import { ButtonSizes } from '../../core/Button/Button.common';

/** Props for modal dialog action buttons */
export type DialogButtonProps = {
  /** Flex direction for button layout */
  buttonDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  /** Cancel button text */
  cancelButtonText?: string;
  /** Cancel button style variant */
  cancelButtonVariant?: ButtonVariants;
  /** Cancel button click handler */
  onClickCancel?: () => void;
  /** Submit button click handler */
  onClickSubmit?: () => void;
  /** Submit button text */
  submitButtonText?: string;
  /** Disable submit button */
  submitButtonDisabled?: boolean;
  /** Show loading spinner on submit button */
  submitButtonLoading?: boolean;
  /** Submit button style variant */
  submitButtonVariant?: ButtonVariants;
};

/** Common props for Modal across web and native */
export type ModalCommonProps = DialogButtonProps & {
  /** Modal content */
  children: React.ReactNode;
  /** Controls modal visibility */
  isOpen: boolean;
  /** Show close (X) icon in header */
  showCloseIcon?: boolean;
  /** Size of close icon button */
  closeIconSize?: ButtonSizes;
  /** Callback when modal is closed */
  onRequestClose: (payload?: unknown) => void;
  /** Header text */
  headerText?: string;
  /** Header text size preset */
  headerSize?: ThemeHeadingSizes;
  /** Custom styles for overlay */
  overlaySx?: React.CSSProperties;
  /** Props passed to content Flex wrapper */
  contentProps?: FlexProps;
  /** Props passed to header Flex wrapper */
  headerProps?: FlexProps;
};

export const usePreventScroll = (isOpen: boolean, modalId: string): void => {
  // Prevent scrolling when modal is open
  const noScroll = new NoScrollController();
  useEffect(() => {
    if (isOpen) {
      noScroll.on(modalId);
    } else {
      noScroll.off(modalId);
    }
    return () => {
      noScroll.off(modalId);
    };
  }, [isOpen, modalId]);
};

export const useReactModalSetup = (appElementSelector: string) => {
  // setAppElement has to be called before render or else we get react-modal errors.
  // useMemo with an empty array second arg will run only once, before render, much like componentWillMount does.
  // react-modal's internals make this method safe to call server-side.
  useMemo(() => {
    ReactModal.setAppElement(appElementSelector);
  }, []);

  const staticHeaderId = useId();
  const modalId = useId();

  return { staticHeaderId, modalId };
};
